import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';

import { applyConsentMode, logImpression, updateConsent } from '../../consentApi';
import { consentDecided } from '../../../services/consent';
import { VIEW, restingViewFor } from './restingView';
import styles from './ConsentBanner.module.sass';

function buildToggles(categories, state) {
  return categories.reduce((acc, category) => {
    acc[category] = Boolean(state && state[category]);
    return acc;
  }, {});
}

function fill(categories, value) {
  return categories.reduce((acc, category) => {
    acc[category] = value;
    return acc;
  }, {});
}

// Real on/off switch: a role="switch" button with a sliding knob, matching
// ui_kit/Toggle visually (track lavender + knob white when on).
function Switch({
  label, checked, onChange, disabled,
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={styles.switch}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.switchKnob} />
    </button>
  );
}

export default function ConsentBanner({ config }) {
  const {
    endpoint, decided, posture, categories, saleSharingCategories, consent, consentMode, urls, copy,
  } = config;

  const restingView = restingViewFor({ decided, posture });

  const [view, setView] = useState(restingView);
  const [toggles, setToggles] = useState(() => buildToggles(categories, consent.categories));
  const [saving, setSaving] = useState(false);
  const bannerRef = useRef(null);

  // Count the banner as shown (once per visitor — the server dedups), so the
  // accept rate has a denominator that includes people who ignore it. Runs on
  // mount only: reopening the settings center later is not a new impression.
  useEffect(() => {
    if (restingView === VIEW.BANNER) { logImpression(endpoint); }
  }, []);

  // The banner is fixed at the bottom and would hide the footer. The page
  // scrolls on <html> with content in a height-constrained <body>, so padding
  // the body reserves no space — a spacer in the content flow (after the
  // footer) does. Its height tracks the banner's live. We also publish the
  // height as a CSS variable so fixed overlays (e.g. the cart drawer) can size
  // to the space above the banner instead of being covered by it.
  useEffect(() => {
    if (view !== VIEW.BANNER || !bannerRef.current) { return undefined; }
    const el = bannerRef.current;
    const footer = document.querySelector('footer');
    const container = footer ? footer.parentElement : document.body;
    const spacer = document.createElement('div');
    spacer.setAttribute('aria-hidden', 'true');
    const apply = () => {
      const height = `${el.offsetHeight}px`;
      spacer.style.height = height;
      document.documentElement.style.setProperty('--consent-banner-height', height);
    };
    apply();
    container.appendChild(spacer);
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      spacer.remove();
      document.documentElement.style.removeProperty('--consent-banner-height');
    };
  }, [view]);

  // Reflect the current/GPC/stored state into Consent Mode immediately, and let
  // any "Your Privacy Choices" link elsewhere on the page reopen the settings.
  useEffect(() => {
    applyConsentMode(consentMode);

    const reopen = (event) => {
      if (event.target.closest('[data-privacy-choices], [href="#your-privacy-choices"]')) {
        event.preventDefault();
        setView(VIEW.MANAGE);
      }
    };
    document.addEventListener('click', reopen);
    return () => document.removeEventListener('click', reopen);
  }, [consentMode]);

  // A page rendered BEFORE the visitor decided can resurface from the
  // back/forward cache with this banner still mounted and its config stale:
  // Chrome restores even Cache-Control: no-store pages (127+), and a change
  // to a non-HTTPOnly cookie like ours does not evict them. On Android the
  // back gesture makes such restores routine, so after Accept/Reject the
  // banner looked like it never went away. When a stale page resurfaces
  // after a decision was recorded, reload it — the server re-renders it
  // without the banner and with the right tags gated in. `decided` pages
  // never re-prompt, so no listener (and no reload loop) there.
  useEffect(() => {
    if (decided) { return undefined; }
    const onPageShow = (event) => {
      if (event.persisted && consentDecided()) { window.location.reload(); }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [decided]);

  // Close the settings center on Escape (the backdrop click handles the mouse).
  useEffect(() => {
    if (view !== VIEW.MANAGE) { return undefined; }
    const onKey = (event) => {
      if (event.key === 'Escape') { setView(restingView); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [view, restingView]);

  const saleSharingOn = useMemo(
    () => saleSharingCategories.every((category) => toggles[category]),
    [toggles, saleSharingCategories],
  );

  const submit = (chosen, source) => {
    if (saving) { return; }
    setSaving(true);
    updateConsent(endpoint, chosen, source)
      .then((payload) => {
        // The choice is recorded: dismiss right away and sync Consent Mode,
        // instead of leaving the banner up until the reload lands (which on a
        // slow mobile connection takes long enough to read as "nothing
        // happened").
        setView(VIEW.HIDDEN);
        applyConsentMode(payload.consent_mode);
        // Tracking loaders are gated server-side, so the choice only takes
        // full effect on a fresh request: newly granted tags render (and
        // load), revoked ones stop rendering. There's no Turbo here — reload
        // is a real navigation that re-renders the page under the new consent.
        window.location.reload();
      })
      .catch(() => setSaving(false));
  };

  const acceptAll = () => submit(fill(categories, true), 'banner');
  const rejectNonEssential = () => submit(fill(categories, false), 'banner');
  const saveChoices = () => submit(toggles, 'settings');

  const setSaleSharing = (value) => setToggles(
    (prev) => ({ ...prev, ...fill(saleSharingCategories, value) }),
  );
  const toggleCategory = (category) => setToggles(
    (prev) => ({ ...prev, [category]: !prev[category] }),
  );
  const closeManage = () => setView(restingView);

  return (
    <div className={styles.root}>
      {view === VIEW.BANNER && (
        <section ref={bannerRef} className={styles.banner} aria-label={copy.eyebrow}>
          <p className={styles.bannerCopy}>
            {copy.body}
            {' '}
            <a className={styles.inlineLink} href={urls.noticeAtCollection}>
              {copy.notice_at_collection}
            </a>
          </p>

          {/* Same variant on all three → equal prominence (CCPA/CPRA) */}
          <div className={styles.btnRow}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOutline}`}
              onClick={acceptAll}
              disabled={saving}
            >
              {copy.accept_all}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOutline}`}
              onClick={rejectNonEssential}
              disabled={saving}
            >
              {copy.reject_non_essential}
            </button>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnOutline}`}
              onClick={() => setView(VIEW.MANAGE)}
              disabled={saving}
            >
              {copy.more_options}
            </button>
          </div>

        </section>
      )}

      {view === VIEW.MANAGE && (
        <div
          className={styles.panelScrim}
          role="presentation"
          onClick={(event) => { if (event.target === event.currentTarget) { closeManage(); } }}
        >
          <div
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label={copy.manage_title}
          >
            <div className={styles.panelHead}>
              <div>
                <h2 className={styles.panelTitle}>{copy.manage_title}</h2>
                <p className={styles.panelIntro}>{copy.manage_intro}</p>
              </div>
              <button
                type="button"
                aria-label={copy.back}
                className={styles.iconBtn}
                onClick={closeManage}
              >
                ×
              </button>
            </div>

            <div className={styles.panelBody}>
              {/* Strictly necessary — not toggleable */}
              <div className={`${styles.row} ${styles.rowFirst}`}>
                <div>
                  <div className={styles.rowLabel}>{copy.essential_label}</div>
                  <div className={styles.rowDesc}>{copy.essential_description}</div>
                </div>
                <span className={styles.alwaysActive}>{copy.essential_always_on}</span>
              </div>

              {/* Master sale / sharing control */}
              <div className={styles.master}>
                <div>
                  <div className={styles.rowLabel}>{copy.sale_sharing_label}</div>
                  <div className={styles.rowDesc}>{copy.sale_sharing_description}</div>
                </div>
                <Switch
                  label={copy.sale_sharing_label}
                  checked={saleSharingOn}
                  onChange={setSaleSharing}
                  disabled={saving}
                />
              </div>

              {/* Category rows */}
              {categories.map((category) => (
                <div className={styles.row} key={category}>
                  <div>
                    <div className={styles.rowLabel}>{copy.categories[category].label}</div>
                    <div className={styles.rowDesc}>
                      {copy.categories[category].description}
                    </div>
                  </div>
                  <Switch
                    label={copy.categories[category].label}
                    checked={Boolean(toggles[category])}
                    onChange={() => toggleCategory(category)}
                    disabled={saving}
                  />
                </div>
              ))}
            </div>

            <div className={styles.panelFoot}>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnOutline}`}
                onClick={closeManage}
                disabled={saving}
              >
                {copy.back}
              </button>
              <button
                type="button"
                className={`${styles.btn} ${styles.btnBlue}`}
                onClick={saveChoices}
                disabled={saving}
              >
                {copy.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
